// packages/safeclaw-web/src/workplace/chat/store/chat-store.ts

import { create } from "zustand";
import type { ChatMessage, InputMode } from "../types";

// ── ID Generator ──

let idCounter = 0;
function nextId(): string {
  return `msg-${++idCounter}-${Date.now()}`;
}

// ── 临时方案：本地消息持久化（后端加历史接口后撤除） ──

const INSTANCE_KEY = "wp-chat-instance-id";
const MSG_KEY_PREFIX = "wp-chat-messages:";
const MAX_PERSISTED = 100;
const ACADEMY_KEY_PREFIX = "wp-chat-academy:";
const ACADEMY_CONTEXT_PREFIX = "wp-academy-context:";

function getSavedInstanceId(): string | null {
  return localStorage.getItem(INSTANCE_KEY);
}

/** 获取课程入口专用的 instanceId（按 sessionId 区分） */
export function getAcademyInstanceId(sessionId: string): string | null {
  return localStorage.getItem(`${ACADEMY_KEY_PREFIX}${sessionId}`);
}

/** 保存课程入口专用的 instanceId（按 sessionId 区分） */
export function setAcademyInstanceId(sessionId: string, id: string): void {
  localStorage.setItem(`${ACADEMY_KEY_PREFIX}${sessionId}`, id);
}

/** 获取普通入口的 instanceId（直接访问 /workplace 时使用） */
export function getNormalInstanceId(): string | null {
  return localStorage.getItem(INSTANCE_KEY);
}

/** 保存课程入口的上下文信息（courseTitle, chapterTitle, description） */
export function saveAcademyContext(
  sessionId: string,
  ctx: { courseTitle?: string; chapterTitle?: string; description?: string },
): void {
  try {
    localStorage.setItem(
      `${ACADEMY_CONTEXT_PREFIX}${sessionId}`,
      JSON.stringify(ctx),
    );
  } catch {
    /* ignore */
  }
}

/** 读取课程入口的上下文信息 */
export function loadAcademyContext(
  sessionId: string,
): {
  courseTitle?: string;
  chapterTitle?: string;
  description?: string;
} | null {
  try {
    const raw = localStorage.getItem(`${ACADEMY_CONTEXT_PREFIX}${sessionId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** 清除课程入口的会话实例和消息（保留课程上下文以便重新注入） */
export function clearAcademySession(sessionId: string): void {
  const instanceId = getAcademyInstanceId(sessionId);
  if (instanceId) {
    localStorage.removeItem(`${MSG_KEY_PREFIX}${instanceId}`);
  }
  localStorage.removeItem(`${ACADEMY_KEY_PREFIX}${sessionId}`);
  // 注意：保留 ACADEMY_CONTEXT_PREFIX 数据，以便 reload 后重新注入 CLAUDE.md + 欢迎语
}

function loadMessages(instanceId: string | null): ChatMessage[] {
  if (!instanceId) return [];
  try {
    const raw = localStorage.getItem(`${MSG_KEY_PREFIX}${instanceId}`);
    if (!raw) return [];
    const msgs = JSON.parse(raw) as ChatMessage[];
    return msgs
      .filter((m) => !m.isPlaceholder && !m.isError)
      .map(({ isNew: _, ...rest }) => rest);
  } catch {
    return [];
  }
}

function persistMessages(instanceId: string | null, messages: ChatMessage[]) {
  if (!instanceId) return;
  try {
    const toSave = messages
      .filter((m) => !m.isPlaceholder && !m.isError)
      .slice(-MAX_PERSISTED)
      .map(({ isNew: _, ...rest }) => rest);
    localStorage.setItem(
      `${MSG_KEY_PREFIX}${instanceId}`,
      JSON.stringify(toSave),
    );
  } catch {
    /* localStorage 满了就算了 */
  }
}

/** 根据实例 ID 加载本地缓存消息（供外部恢复会话时使用） */
export function loadMessagesForInstance(instanceId: string): ChatMessage[] {
  return loadMessages(instanceId);
}

// ── Types ──

export interface ChatState {
  instanceId: string | null;
  messages: ChatMessage[];
  aiLoading: boolean;
  pendingAiMessageId: string | null;
  inputMode: InputMode;
  inputPlaceholder: string;
  /** 后端历史是否已加载完成 */
  historyLoaded: boolean;
  /** 递增计数器，变化时触发滚动到底部 */
  scrollToken: number;
  /** SSE 连接断开（重连耗尽） */
  disconnected: boolean;
}

export interface ChatActions {
  setInstanceId: (id: string) => void;
  pushMessage: (msg: Omit<ChatMessage, "id" | "timestamp">) => ChatMessage;
  clearMessages: () => void;
  setAiLoading: (loading: boolean) => void;
  setDisconnected: (v: boolean) => void;
  updateToolResult: (
    toolId: string,
    result: string,
    status?: "running" | "done" | "error",
  ) => void;
  pushPlaceholderMessage: (id: string) => void;
  insertMessageBefore: (
    beforeId: string,
    msg: Omit<ChatMessage, "id" | "timestamp">,
  ) => void;
  updateMessage: (
    id: string,
    updater: (msg: ChatMessage) => ChatMessage,
  ) => void;
  removeMessage: (id: string) => void;
  setPendingAiMessageId: (id: string | null) => void;
  setInputMode: (mode: InputMode, placeholder?: string) => void;
  /** 清除指定消息的 isNew 标记，防止 Virtuoso 重挂载时重播动画 */
  clearIsNew: (id: string) => void;
  /** 彻底重置聊天（清除 instanceId + 消息 + localStorage） */
  resetChat: () => void;
  /** 标记后端历史加载完成，并用后端数据替换本地缓存。snapshotLen 为调用前消息数组中属于"旧缓存"的长度 */
  finishHistoryLoad: (
    backendMessages: ChatMessage[],
    snapshotLen: number,
  ) => void;
  /** 触发滚动到底部 */
  requestScrollToBottom: () => void;
}

// ── Store ──

const savedInstanceId = getSavedInstanceId();

export const useChatStore = create<ChatState & ChatActions>((set, get) => ({
  // ── State ──
  instanceId: savedInstanceId,
  messages: loadMessages(savedInstanceId),
  aiLoading: false,
  pendingAiMessageId: null,
  inputMode: "free",
  inputPlaceholder: "输入消息...",
  historyLoaded: false,
  scrollToken: 0,
  disconnected: false,

  // ── Message Actions ──
  setInstanceId: (id) => {
    localStorage.setItem(INSTANCE_KEY, id);
    set({ instanceId: id });
  },

  pushMessage: (msg) => {
    const message: ChatMessage = {
      ...msg,
      id: nextId(),
      timestamp: Date.now(),
    };
    set((s) => {
      const messages = [...s.messages, message];
      persistMessages(s.instanceId, messages);
      return { messages };
    });
    return message;
  },

  clearMessages: () => {
    const { instanceId } = get();
    if (instanceId) {
      localStorage.removeItem(`${MSG_KEY_PREFIX}${instanceId}`);
    }
    set({ messages: [] });
  },

  setAiLoading: (loading) => set({ aiLoading: loading }),
  setDisconnected: (v) => set({ disconnected: v }),

  updateToolResult: (toolId, result, status = "done") => {
    set((s) => {
      const msgs = s.messages.map((msg) => {
        let changed = false;
        const newBlocks = msg.blocks.map((block) => {
          if (block.type === "tool_call") {
            const newTools = block.tools.map((t) => {
              if (t.id === toolId) {
                changed = true;
                return { ...t, result, status };
              }
              return t;
            });
            if (changed) return { ...block, tools: newTools };
          }
          return block;
        });
        return changed ? { ...msg, blocks: newBlocks } : msg;
      });
      if (msgs !== s.messages) {
        persistMessages(s.instanceId, msgs);
        return { messages: msgs };
      }
      return {};
    });
  },

  pushPlaceholderMessage: (id) => {
    const message: ChatMessage = {
      id,
      role: "ai",
      blocks: [],
      timestamp: Date.now(),
      isNew: true,
      isPlaceholder: true,
    };
    set((s) => {
      const messages = [...s.messages, message];
      return { messages, pendingAiMessageId: id, aiLoading: true };
    });
  },

  insertMessageBefore: (beforeId, msg) => {
    const message: ChatMessage = {
      ...msg,
      id: nextId(),
      timestamp: Date.now(),
    };
    set((s) => {
      const idx = s.messages.findIndex((m) => m.id === beforeId);
      if (idx === -1) {
        const messages = [...s.messages, message];
        persistMessages(s.instanceId, messages);
        return { messages };
      }
      const messages = [
        ...s.messages.slice(0, idx),
        message,
        ...s.messages.slice(idx),
      ];
      persistMessages(s.instanceId, messages);
      return { messages };
    });
  },

  updateMessage: (id, updater) => {
    set((s) => {
      const messages = s.messages.map((msg) =>
        msg.id === id ? updater(msg) : msg,
      );
      persistMessages(s.instanceId, messages);
      return { messages };
    });
  },

  removeMessage: (id) => {
    set((s) => {
      const messages = s.messages.filter((m) => m.id !== id);
      persistMessages(s.instanceId, messages);
      return {
        messages,
        pendingAiMessageId:
          s.pendingAiMessageId === id ? null : s.pendingAiMessageId,
      };
    });
  },

  setPendingAiMessageId: (id) => set({ pendingAiMessageId: id }),

  // ── Input Actions ──
  setInputMode: (mode, placeholder) => {
    set({
      inputMode: mode,
      inputPlaceholder: placeholder ?? "输入消息...",
    });
  },

  clearIsNew: (id) => {
    set((s) => {
      const idx = s.messages.findIndex((m) => m.id === id);
      if (idx === -1 || !s.messages[idx].isNew) return {};
      const msgs = [...s.messages];
      msgs[idx] = { ...msgs[idx], isNew: undefined };
      return { messages: msgs };
    });
  },

  resetChat: () => {
    const { instanceId } = get();
    if (instanceId) {
      localStorage.removeItem(`${MSG_KEY_PREFIX}${instanceId}`);
    }
    localStorage.removeItem(INSTANCE_KEY);
    set({
      instanceId: null,
      messages: [],
      pendingAiMessageId: null,
      aiLoading: false,
      historyLoaded: false,
    });
  },

  finishHistoryLoad: (backendMessages, snapshotLen) => {
    const { instanceId, messages: current } = get();
    if (backendMessages.length > 0) {
      // snapshotLen 之前的是旧本地缓存（要被后端数据替换），之后的是加载期间新增的（保留）
      const newMsgs = current.slice(snapshotLen);
      const merged = [...backendMessages, ...newMsgs];
      persistMessages(instanceId, merged);
      set({
        messages: merged,
        historyLoaded: true,
        scrollToken: get().scrollToken + 1,
      });
    } else {
      set({ historyLoaded: true });
    }
  },

  requestScrollToBottom: () => {
    set({ scrollToken: get().scrollToken + 1 });
  },
}));
